//
// # Layout Schema
//

import { Change } from "CoreUtils";
import * as core from "./core";
import { SchemaVersion } from "./version";

// ============================================================================
// Main Layout Type
// ============================================================================

export type Layout = {
  schemaVersion: SchemaVersion;
  contentKind: "layout";
  ident: core.Ident;
  abs: Abstract;
  impl?: Impl;

  pdkId?: string;
  stackId?: string;

  properties?: Array<core.Property>;
  schematicImpl?: core.Ident;

  // Optional override for the GDS cell name. If set, the GDS exporter will use
  // this name instead of the auto-generated name from the ident. This is needed
  // for PDK compatibility where the LVS tool expects specific cell names (e.g.,
  // "symindp_pcell*" for inductors in GF22).
  gdsCellName?: string;

  // Opaque OpenAccess passthrough content captured at OA import (see
  // `OaResidue`). Only present on OA-originated layouts.
  oaResidue?: OaResidue;
};

// # OpenAccess Residue
//
// Opaque passthrough for OA content GenAlpha does not model. The importer
// captures it, the exporter splices it back, nothing else interprets it — which
// is what makes an OA -> GA -> OA round trip a fixpoint for content the editor
// cannot represent.
//
// Every payload is a base64-encoded NOA protobuf message, one fixed type per
// field. Bytes rather than structured data, deliberately: the NOA schema stays
// out of the GA schema, and unknown content cannot be half-interpreted.
//
// As layout objects graduate to first-class GA support, their fields here are
// removed and their content maps structurally instead.
export interface OaResidue {
  // Residue format version. Bump on any change to field meanings; the OA
  // exporter refuses versions it doesn't know rather than guessing. Version 2
  // keys `pinTerms` over pin shapes, Terminals and `internalConnection`.
  version: 2;
  designProperties?: Array<string>; // base64 noa.Property
  designAppdefs?: Array<string>; // base64 noa.AppDef
  // No longer populated at import: PCell super-master params cannot be written
  // back, so carrying them preserved nothing and made the GA file unstable.
  // Retained so older files still validate and splice. Replaced by
  // `noa.Design.is_super_master` plus a reader-side `unsupported` entry.
  designParams?: Array<string>; // base64 noa.Param
  blockProperties?: Array<string>; // base64 noa.Property
  blockAppdefs?: Array<string>; // base64 noa.AppDef
  nets?: Array<string>; // base64 noa.Net
  terms?: Array<string>; // base64 noa.Term (includes pins)
  // Which OA terms we imported GA pins from, plus a content key over the pins we
  // made (G4, return leg).
  //
  // Same arrangement as `routeKeys`, one level more involved: ONE GA pin is TWO
  // residue entries — an `oaTerm` in `terms` and its figure in `shapes` — which
  // must splice or drop together, since an orphaned figure fails the writer.
  // Hence the unit of decision is the TERM.
  //
  // At export, recompute the key from the live `abs.pins`. Match splices the
  // term and its figures verbatim; mismatch drops both and regenerates those
  // pins. A term absent from this list splices unconditionally.
  pinTerms?: Array<PinTermImport>;
  // Whole NOA shapes with no lossless GA form (text, pathSeg/line/dot/donut,
  // styled paths, sentinel purposes, shapes carrying net/pin associations or
  // properties/appdefs). NOT also in `impl.elems` — each OA shape lives on
  // exactly one side, so export needs no dedup step.
  shapes?: Array<string>; // base64 noa.Shape
  // OA routes and block-level vias, carried whole.
  //
  // Routes are the ONE exception to this field's XOR discipline, made safe by
  // `routeKeys`: an importable route lands both in `impl.routes` (visible and
  // editable) and here (via-def names, via params, seg styles, status survive).
  // The exporter picks per route (G18b).
  //
  // `vias` is unrelated to a route's own vias — a route-member via lives inside
  // the route blob. These belong to no route, GA has no model for them, and a
  // route edit must not touch them.
  routes?: Array<string>; // base64 noa.Route
  // A content-derived key per entry of `routes`, same length and order.
  //
  // `routes[i]` splices verbatim when `routeKeys[i]` still matches a GA route,
  // which proves the wire has not been edited. Matching nothing means it changed
  // or was deleted, so the blob is dropped and the wire regenerates. `null`
  // means the route was never expressible as a GA polyline, and always splices.
  //
  // A derived key rather than a flag purged on edit: a purge would have to be
  // installed on every path that can mutate a route — IDE, agent tools, depot
  // imports, a CRDT merge — and missing one emits stale geometry. A pure
  // function of the layout cannot be bypassed.
  //
  // Absent (with `routes` present) is the pre-G18b shape: everything splices.
  routeKeys?: Array<string | null>;
  vias?: Array<string>; // base64 noa.Via
  // Per-instance OA extras (connections, properties, appdefs, params, pin),
  // keyed by instance name; the value is the full original base64
  // noa.Instance, from which the exporter merges the fields GA doesn't model.
  instances?: Record<string, string>;
  // OA blockages, carried whole. GA's `abs.blockages` is narrower — a layer plus
  // a rectangle, with no blockage TYPE — and a survey of 23,279 designs found
  // 821,985 blockages, 89% `fill` and 10% `routing`. Collapsing those onto a
  // type-less GA blockage would lose the distinction silently.
  blockages?: Array<string>; // base64 noa.Blockage
  // The OA place-and-route boundary, carried whole. Singular, hence not an
  // array. GA's `abs.outline` is a Bbox where OA's is a polygon, so an
  // OA-originated boundary is not imported into it. Present on 50.5% of the
  // surveyed corpus and previously dropped entirely.
  prBoundary?: string; // base64 noa.PRBoundary
}

// One OA `oaTerm` that GenAlpha pins were imported from. See
// `OaResidue.pinTerms`.
export interface PinTermImport {
  /** The OA term's name. Matches an entry in `OaResidue.terms`. */
  term: string;
  /** The `abs.pins` names this term produced. */
  pins: Array<string>;
  /**
   * Content key over those pins as imported, from noa-conv's `pins_key`: their
   * names, Terminals, `internalConnection`s and shapes. Excludes `LayoutPin.id`,
   * for the reason `routeKeys` excludes `Route.id`: `core.Id` does not survive a
   * round trip, so including it would invalidate every key on every reload and
   * nothing would ever splice.
   */
  key: string;
}

export interface Abstract {
  bbox: core.Bbox; // Bounding Box
  outline?: core.Bbox;
  pins: Array<LayoutPin>; // Pins
  blockages: Array<Blockage>; // Blockages/ Obstructions
}

export type Impl = {
  elems: Array<core.LayeredElement>; // Implementation Content
  instances: Array<Instance>;
  routes: Array<Route>; // Routes
  conns: Array<Conn>; // Connections
};

interface LayoutInstance {
  name: string; // Instance Name
  target: core.Ident; // Target Layout being instantiated
  place?: Place; // Placement. Undefined means "not placed"
  id?: core.Id;
  params?: Record<string, unknown>; // Optional parameters for generator-based instances
}
export type Instance = LayoutInstance;

// Instance Placement
export type Place = ScalarPlace | SegmentedPlace | ArrayPlace;

// Regular "scalar" instance with a single transform.
export type ScalarPlace = {
  kind: "scalar";
  xform: LayoutXform;
};

// Segments, for multi-segment instances
export type SegmentedPlace = {
  kind: "segments";
  segments: Array<Segment>;
};

// Each segment can optionally override the instance-level target with its own
// generated cell. Used for abutted transistor arrays where edge cells have
// different dummy counts than interior cells.
export type Segment = (LayoutXform & { target?: core.Ident }) | null;

// Uniformly arrayed instances
export type ArrayPlace = { kind: "array"; array: ArrayXform };

export interface Blockage {
  /**
   * GenAlpha layer ID (references PDKLayer.number)
   * Changed in v6: was string layer name, now numeric ID
   * @type int
   */
  layer: number;
  bbox: core.Bbox; // Outline
  id?: core.Id;
}

// # Connection
// "Per net" connection
// Net name to list of instance and layout ports it
export interface Conn {
  net: string; // Net (name). FIXME: we want a structured `Net` too?
  ports: Array<PortConn>; // Ports (instances and layout-level pins) to which it is connected
}

export type PortConn =
  | { kind: "inst"; inst: string; port: string } // Instance Port
  | { kind: "pin"; pin: string }; // Layout-Level Pin/ Port

// # Transform for a Layout Instance
// Supports reflection and rotation in 90-degree increments
export interface LayoutXform {
  loc: core.Point; // Location of target's origin
  reflect: {
    horiz: boolean;
    vert: boolean;
  };
  rotation?: core.Rotation; // Rotation in 90-degree increments (0, 90, 180, 270). Defaults to 0.
}

// # Array Transform
// For uniformly arrayed instances
// The base `xform` is the origin of the first instance.
// Reflection is applied to the entire array
// (i.e. the array is reflected, not each instance)
export type ArrayXform = LayoutXform & {
  nrows: number; // Number of rows
  ncols: number; // Number of columns
  dx: number; // X spacing
  dy: number; // Y spacing
};

/**
 * Physical layout pin: real metal of one Terminal.
 *
 * A pin is its `shapes`, of which it has at least one. It has no point or
 * layer of its own: `pinLoc` and `pinLayer` (`./pins`) answer them from a
 * one-shape pin, and a caller that can meet a multi-shape pin reads `shapes`.
 * Every pin has a Terminal, `pinTerminal` (`./pins`). `internalConnection` is
 * optional in the type, but every stored pin has it, and a pin without it fails
 * the pin rules.
 */
export interface LayoutPin {
  /** The pin's name, unique in the layout. Connections refer to the pin by it. */
  name: string;
  /** The Terminal this pin belongs to; when absent, the pin's name (`pinTerminal`). */
  terminal?: string;
  internalConnection?: InternalConnection;
  shapes: [LayoutPinShape, ...LayoutPinShape[]];
  /**
   * Semantic pin type for power routing and analysis.
   * If not specified, defaults to "signal" at the consumer level.
   * - "signal": Regular signal pin (default)
   * - "power": Power supply pin (e.g., VDD, AVDD)
   * - "ground": Ground pin (e.g., VSS, GND)
   * - "clock": Clock pin
   */
  pinType?: core.PinType;
  id?: core.Id;
}

/** Joined inside the cell to another pin of its Terminal ("weak") or not ("none"). */
export type InternalConnection = "weak" | "none";

export interface LayoutPinShape {
  /** @type int */
  layer: number;
  /** @type int */
  purpose: number;
  shape: PinShape;
}

export type PinShape =
  | { kind: "rect"; bbox: core.Bbox }
  | { kind: "polygon"; points: core.Point[] }
  | { kind: "path"; points: core.Point[]; style: core.PathStyle }
  /**
   * A point with no area: a pin stored before pins had shapes, whose only
   * geometry was a point. It is not metal, so it fails pin rule 1.
   */
  | { kind: "point"; at: core.Point };

/** A point along a route path with its associated layer */
export interface RoutePoint {
  point: core.Point;
  /** Numeric PDK layer ID */
  layerId: number;
  /**
   * Width (nm) of the segment leaving this point; absent/null = layer default.
   * See route_width.ts for the outgoing-segment convention and helpers.
   * @type int
   */
  width?: number | null;
}

export interface Route {
  net: string; // FIXME: we want a structured `Net` too?
  /**
   * The path of the route as a sequence of points with layer info.
   * Vias are represented by consecutive points at the same location with different layers.
   */
  path: Array<RoutePoint>;
  id?: core.Id;
  /**
   * If true, this route should not be ripped up by subsequent routing operations.
   * Power mesh routes, manually verified routes, etc. should set this to true.
   */
  locked?: boolean;
}

// ============================================================================
// Change Types
// ============================================================================

// Standard child-based changes (add/update/remove with ID)
export type LayoutChildChange = Change<LayoutChild> & { contentKind: "layout" };

// Property-based changes (set/remove by name, not ID)
export type LayoutPropertyChange =
  | { contentKind: "layout"; changeKind: "set-property"; data: core.Property }
  | {
      contentKind: "layout";
      changeKind: "remove-property";
      data: { name: string };
    };

// Outline changes (set/remove the layout cell's outline)
export type LayoutOutlineChange =
  | {
      contentKind: "layout";
      changeKind: "set-outline";
      data: { outline: core.Bbox };
    }
  | {
      contentKind: "layout";
      changeKind: "remove-outline";
    };

// Combined layout change type
export type LayoutChange =
  | LayoutChildChange
  | LayoutPropertyChange
  | LayoutOutlineChange;

export type LayoutChild =
  | (Instance & { targetKind: "instance" })
  | (LayoutPin & { targetKind: "pin" })
  | (Blockage & { targetKind: "blockage" })
  | (Route & { targetKind: "route" });
